// @ts-nocheck — migrated from JS; reactstrap / react-select / layout prop friction.
import React, { Fragment, useMemo } from "react";
import IntlMessages from "Util/IntlMessages";
import { useIntl } from "react-intl";
import {
  Row,
  Card,
  CardBody,
  CardTitle,
  Badge,
  Button
} from "reactstrap";
import { Colxx, Separator } from "Components/CustomBootstrap";
import BreadcrumbContainer from "Components/BreadcrumbContainer";
import PerfectScrollbar from "react-perfect-scrollbar";
import {
  PERFECT_SCROLLBAR_MAX_100,
  PERFECT_SCROLLBAR_MAX_280
} from "Constants/perfectScrollbarLayout";
import ReactTable from "react-table";
import DataTablePagination from "Components/DataTables/pagination";
import Chart from "react-apexcharts";
import { Sparklines, SparklinesLine } from "react-sparklines";
import { useKpiSparklineLineStyle } from "Hooks/useKpiSparklineLineStyle";
import { ReactifyChartColors } from "Util/ReactifyChartColors";
import { DASHBOARD_HR_ORG_INITIALS } from "Constants/reactifyInlineStyles";
import { addCommas } from "Util/Utils";
import data from "Data/dashboardHr.json";

import "react-table/react-table.css";

function HrPeopleDashboard() {
  const colors = useMemo(() => ReactifyChartColors(), []);
  const kpiSparkLineStyle = useKpiSparklineLineStyle(colors.ReactifyChartColor1);
  const intl = useIntl();

  const funnelOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: "78%",
          distributed: true,
          borderRadius: 4
        }
      },
      colors: [
        c.ReactifyChartColor1,
        c.ReactifyChartColor2,
        c.ReactifyChartColor3,
        c.ReactifyChartColor4,
        c.ReactifyChartColor5
      ],
      dataLabels: {
        enabled: true,
        formatter: (val, ctx) => {
          const i = ctx.dataPointIndex;
          if (i === 0) return addCommas(val);
          const prev = data.hiringFunnel[i - 1].value;
          const pct = Math.round((val / prev) * 1000) / 10;
          return `${addCommas(val)} (${pct}%)`;
        }
      },
      xaxis: {
        categories: data.hiringFunnel.map(f => f.step),
        labels: { style: { colors: c.primaryColor } }
      },
      grid: { borderColor: c.separatorColor },
      legend: { show: false },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const tthOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false }, zoom: { enabled: false } },
      stroke: { width: 3, curve: "smooth" },
      colors: [c.ReactifyChartColor1],
      dataLabels: { enabled: false },
      xaxis: {
        categories: data.timeToHireTrend.months,
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: {
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => `${v}d`
        }
      },
      grid: { borderColor: c.separatorColor, strokeDashArray: 4 },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const deptOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, columnWidth: "55%" } },
      colors: [c.ReactifyChartColor1, c.ReactifyChartColor3],
      dataLabels: { enabled: false },
      xaxis: {
        categories: data.byDepartment.map(d => d.dept),
        labels: {
          style: { colors: c.primaryColor },
          rotate: -20,
          rotateAlways: true
        }
      },
      yaxis: {
        labels: {
          style: { colors: c.primaryColor },
          formatter: v => addCommas(v)
        }
      },
      legend: { position: "top" },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const ptoOptions = useMemo(() => {
    const c = colors;
    return {
      chart: { toolbar: { show: false } },
      plotOptions: { bar: { borderRadius: 4, horizontal: true, barHeight: "72%" } },
      colors: [c.ReactifyChartColor2],
      dataLabels: { enabled: true, formatter: v => `${v}%` },
      xaxis: {
        categories: data.ptoByTeam.map(p => p.team),
        labels: { style: { colors: c.primaryColor } }
      },
      yaxis: { labels: { style: { colors: c.primaryColor } } },
      grid: { borderColor: c.separatorColor },
      tooltip: { theme: "light" }
    };
    }, [colors]);

  const diversityOptions = useMemo(() => {
    const c = colors;
    return {
      labels: data.diversity.labels,
      colors: [
        c.ReactifyChartColor1,
        c.ReactifyChartColor2,
        c.ReactifyChartColor3,
        c.ReactifyChartColor4
      ],
      legend: { position: "bottom" },
      plotOptions: { pie: { donut: { size: "68%" } } },
      dataLabels: { enabled: true }
    };
    }, [colors]);

  const rolesColumns = useMemo(() => {
    const { formatMessage } = intl;
    return [
      {
        Header: formatMessage({ id: "hr.col.title" }),
        accessor: "title",
        Cell: p => <span className="font-weight-medium">{p.value}</span>
      },
      {
        Header: formatMessage({ id: "hr.col.dept" }),
        accessor: "dept"
      },
      {
        Header: formatMessage({ id: "hr.col.location" }),
        accessor: "location"
      },
      {
        Header: formatMessage({ id: "hr.col.stage" }),
        accessor: "stage",
        Cell: p => <Badge color="info">{p.value}</Badge>
      },
      {
        Header: formatMessage({ id: "hr.col.days" }),
        accessor: "daysOpen",
        Cell: p => `${p.value}d`
      }
    ];
    }, [intl]);

  const k = data.kpis;

  return (
      <div className="dashboard-vertical dashboard-vertical--hr">
        <Fragment>
          <Row>
            <Colxx xxs="12">
              <BreadcrumbContainer
                heading={<IntlMessages id="menu.hr-people" />}
              />
              <Separator className="mb-5" />
            </Colxx>
          </Row>

          <Row className="mb-2">
            <Colxx lg="8" className="mb-3">
              <Card className="border-primary border-opacity-25 bg-light h-100">
                <CardBody className="py-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h6 className="mb-0 text-muted text-uppercase small">
                      <IntlMessages id="hr.hero.headline" />
                    </h6>
                    <Button color="primary" size="sm" outline>
                      <IntlMessages id="hr.hero.open-ats" />
                    </Button>
                  </div>
                  <p className="text-muted small mb-0">
                    <IntlMessages id="hr.hero.sub" />
                  </p>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="4" className="mb-3">
              <Card className="border-primary border-opacity-25 bg-light h-100">
                <CardBody className="py-3">
                  <p className="text-muted mb-2 small text-uppercase">
                    <IntlMessages id="hr.section.news" />
                  </p>
                  <PerfectScrollbar style={PERFECT_SCROLLBAR_MAX_100}>
                    <ul className="list-unstyled mb-0 small">
                      {data.announcements.map((a, i) => (
                        <li key={i} className="mb-2">
                          <span className="text-muted me-2">{a.date}</span>
                          {a.title}
                        </li>
                      ))}
                    </ul>
                  </PerfectScrollbar>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <Sparklines data={data.headcountSpark} margin={6}>
                    <SparklinesLine style={kpiSparkLineStyle} />
                  </Sparklines>
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="hr.kpi.headcount" />
                  </p>
                  <h4 className="mb-0">{addCommas(k.headcount)}</h4>
                  <span className="text-success small">+{k.headcountDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-briefcase text-primary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="hr.kpi.open-roles" />
                  </p>
                  <h4 className="mb-0">{k.openRoles}</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-clock-o text-info mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="hr.kpi.tth" />
                  </p>
                  <h4 className="mb-0">{k.avgTimeToHireDays}d</h4>
                  <span className="text-success small">{k.ttHireDelta}d</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-random text-warning mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="hr.kpi.turnover" />
                  </p>
                  <h4 className="mb-0">{k.turnoverPct}%</h4>
                  <span className="text-success small">{k.turnoverDelta}%</span>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-heart text-danger mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="hr.kpi.engagement" />
                  </p>
                  <h4 className="mb-0">{k.engagementScore}</h4>
                </CardBody>
              </Card>
            </Colxx>
            <Colxx sm="6" md="4" lg="2" className="mb-4">
              <Card className="h-100 dashboard-vertical__kpi-card">
                <CardBody>
                  <i className="fa fa-calendar text-secondary mb-2 d-block" />
                  <p className="text-muted mb-1 small text-uppercase">
                    <IntlMessages id="hr.kpi.reviews" />
                  </p>
                  <h4 className="mb-0">{k.pendingReviews}</h4>
                  <span className="text-muted small">
                    <IntlMessages id="hr.kpi.out" /> {k.outToday}
                  </span>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="5" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="hr.chart.funnel" />
                  </CardTitle>
                  <Chart
                    options={funnelOptions}
                    series={[{ data: data.hiringFunnel.map(f => f.value) }]}
                    type="bar"
                    height={340}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="7" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="hr.chart.tth" />
                  </CardTitle>
                  <Chart
                    options={tthOptions}
                    series={[
                      {
                        name: "Days",
                        data: data.timeToHireTrend.days
                      }
                    ]}
                    type="line"
                    height={340}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="7" className="mb-4">
              <Card className="dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="hr.chart.dept" />
                  </CardTitle>
                  <Chart
                    options={deptOptions}
                    series={[
                      {
                        name: "Headcount",
                        data: data.byDepartment.map(d => d.count)
                      },
                      {
                        name: "Open reqs",
                        data: data.byDepartment.map(d => d.open)
                      }
                    ]}
                    type="bar"
                    height={300}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="5" className="mb-4">
              <Card className="h-100 dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="hr.chart.diversity" />
                  </CardTitle>
                  <Chart
                    options={diversityOptions}
                    series={data.diversity.values}
                    type="donut"
                    height={300}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="5" className="mb-4">
              <Card className="dashboard-vertical__chart-card">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="hr.chart.pto" />
                  </CardTitle>
                  <Chart
                    options={ptoOptions}
                    series={[
                      {
                        name: "PTO used",
                        data: data.ptoByTeam.map(p => p.usedPct)
                      }
                    ]}
                    type="bar"
                    height={280}
                    width="100%"
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="7" className="mb-4">
              <Card className="h-100">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="hr.section.org" />
                  </CardTitle>
                  <Row>
                    {data.orgHighlights.map((o, i) => (
                      <Colxx md="4" key={i} className="mb-3">
                        <div className="border rounded p-3 text-center h-100">
                          <div
                            className="rounded-circle bg-primary text-white d-inline-flex align-items-center justify-content-center mb-2"
                            style={DASHBOARD_HR_ORG_INITIALS}
                          >
                            {o.initials}
                          </div>
                          <div className="font-weight-medium">{o.name}</div>
                          <div className="text-muted small">{o.title}</div>
                          <Badge color="light" className="text-dark mt-2">
                            {o.reports}{" "}
                            <IntlMessages id="hr.org.reports" />
                          </Badge>
                        </div>
                      </Colxx>
                    ))}
                  </Row>
                </CardBody>
              </Card>
            </Colxx>
          </Row>

          <Row>
            <Colxx lg="7" className="mb-4">
              <Card>
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="hr.table.roles" />
                  </CardTitle>
                  <ReactTable
                    defaultPageSize={5}
                    data={data.openRoles}
                    columns={rolesColumns}
                    minRows={0}
                    PaginationComponent={DataTablePagination}
                  />
                </CardBody>
              </Card>
            </Colxx>
            <Colxx lg="5" className="mb-4">
              <Card className="h-100">
                <CardBody>
                  <CardTitle>
                    <IntlMessages id="hr.section.reviews" />
                  </CardTitle>
                  <PerfectScrollbar style={PERFECT_SCROLLBAR_MAX_280}>
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>
                            <IntlMessages id="hr.col.name" />
                          </th>
                          <th>
                            <IntlMessages id="hr.col.role" />
                          </th>
                          <th>
                            <IntlMessages id="hr.col.due" />
                          </th>
                          <th>
                            <IntlMessages id="hr.col.type" />
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.reviewsDue.map((r, i) => (
                          <tr key={i}>
                            <td className="font-weight-medium">{r.name}</td>
                            <td className="small">{r.role}</td>
                            <td>{r.due}</td>
                            <td>
                              <Badge color="secondary">{r.type}</Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </PerfectScrollbar>
                </CardBody>
              </Card>
            </Colxx>
          </Row>
        </Fragment>
      </div>
    );
}

export default HrPeopleDashboard;
